import { Cross } from '@saleshandy/icons';
import { debounce } from 'lodash';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { Spinner } from 'react-bootstrap';
import { useSelector } from 'react-redux';
import Lottie from 'lottie-react';
import { accessibleOnClick } from '../../../../../../../shared/utils/accessible-on-click';
import { RootState } from '../../../../../../../store/root-reducer';
import { capitalizeFirstLetter } from '../../../../../../leads/helpers/helper';
import { OnBoardingStepProps } from '../../../type';
import { slides } from '../util/helper';
import { executeOnRequestStatus } from '../../../../../../../shared/utils';
import AiVariant3StarIcon from '../../../../../../../assets/json/lottieanimation/ai-variant-3-star-icon.json';
import GlobalRestrictionModal from '../../../../../../home/components/global-restriction-modal';
import { getIsDemoAccount } from '../../../../../../../shared/utils/user-details';
import S3StaticImage from '../../../../../../../shared/utils/s3-static-images';
import AiCreditCapsule from '../../../../../../prospect/components/ai-col-enrichment-modal/components/ai-credit-capsule/ai-credit-capsule';
/**
 *
 * @description
 * This component renders the onboarding step for the AI Sequence Copilot. It includes a welcome message,
 * a carousel showcasing helpful onboarding content, and a "Get Started" CTA to begin the AI email sequence generation process.
 * It also listens for the status of a backend request and displays chat messages accordingly when the request succeeds.
 *
 * The component auto-rotates through onboarding slides, allows manual navigation between them,
 * and handles cleanup appropriately. It also resets the request status once onboarding completes.
 *
 * @param handleModalClose - Function to handle closing the onboarding modal.
 * @param startAiSeqGeneratorProcess - Function to initiate the AI sequence generation process.
 *
 * @returns {JSX.Element} React Component
 *
 */

const OnBoardingStep: React.FC<OnBoardingStepProps> = ({
  handleModalClose = () => {},
  startAiSeqGeneratorProcess = () => {},
  isProspectEnrichmentEnabledMeta = false,
  isOldAgencyUser = false,
}) => {
  //* states */
  const [activeSlide, setActiveSlide] = useState<number>(0);
  const [isLoadingActive, setIsLoadingActive] = useState<boolean>(false);
  const [
    showGlobalRestrictionModal,
    setShowGlobalRestrictionModal,
  ] = useState<boolean>(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const { firstName, lastName } = useSelector(
    (state: RootState) => state?.myProfile?.myProfile,
  );
  const { status } = useSelector(
    (state: RootState) => state?.sequence?.saveAiSeqGeneratorStepDetailsRequest,
  );
  // const dispatch = useDispatch();
  const startAutoSlide = useCallback(() => {
    if (timerRef?.current) clearInterval(timerRef?.current);
    timerRef.current = setInterval(() => {
      setActiveSlide((prevSlide) => (prevSlide + 1) % slides?.length);
    }, 2200);
  }, []);

  // Auto rotate carousel slides
  useEffect(() => {
    if (slides?.length === 0) return;
    startAutoSlide();
    // eslint-disable-next-line consistent-return
    return () => timerRef?.current && clearInterval(timerRef?.current);
  }, [slides]);

  useEffect(() => {
    executeOnRequestStatus({
      status,
      onSuccess: () => {
        setIsLoadingActive(false);
      },
      onPending: () => {
        setIsLoadingActive(true);
      },
      onFailed: () => {
        setIsLoadingActive(false);
      },
    });
  }, [status]);

  // Handle manual navigation
  const handleSlideChange = useCallback(
    (index: any) => {
      setActiveSlide(index);
      startAutoSlide();
    },
    [setActiveSlide],
  );

  // Redirect function for Get Started button
  const handleGetStarted = useCallback(
    debounce(() => {
      if (getIsDemoAccount()) {
        setShowGlobalRestrictionModal(true);
        return;
      }
      startAiSeqGeneratorProcess();
    }, 600),
    [startAiSeqGeneratorProcess],
  );

  return (
    <div className="onboarding-container">
      {/* Left side - Carousel */}
      <div className="content-section">
        <div className="content-carousel-divider">
          <div className="header">
            <Lottie
              animationData={AiVariant3StarIcon}
              loop
              className="ai-3-star-animated-icon"
            />
            <span className="title">AI Sequence Copilot</span>
            <span className="beta-tag">Beta</span>
          </div>

          <div className="onboading-greeting">
            <span>Hello,</span>
            {capitalizeFirstLetter(firstName ?? '')}&nbsp;
            {capitalizeFirstLetter(lastName ?? '')}
          </div>

          <div className="onboarding-content">
            <p>
              Writing emails doesn't have to be hard. I'm here to guide you
              through every step, ensuring your messages are engaging and
              effective.
            </p>
            <p>Shall we get started?</p>
          </div>

          <div className="cta-section">
            <button
              type="button"
              onClick={handleGetStarted}
              className={`get-started-btn ${
                isLoadingActive ? 'disable-on-boarding-btn' : ''
              }`}
              disabled={isLoadingActive}
            >
              {isLoadingActive ? (
                <Spinner animation="border" className="spinner-icon" />
              ) : (
                "Yes, Let's get started"
              )}
            </button>
          </div>
        </div>
        <div className="slide-indicators-wrapper">
          <div className="slide-indicators">
            {slides?.map((_, index) => (
              <button
                key={`carousel-${index + 1}`}
                type="button"
                onClick={() => handleSlideChange(index)}
                className={`indicator ${index === activeSlide ? 'active' : ''}`}
                aria-label={`Go to slide ${index + 1}`}
              />
            ))}
          </div>
          {isProspectEnrichmentEnabledMeta && !isOldAgencyUser && (
            <AiCreditCapsule
              credits="50"
              unit="AI Sequence"
              showApproximation
              showBorder={false}
              className="slide-indicators-credit-capsule"
              iconClassName="slide-indicators-credit-capsule-icon"
            />
          )}
        </div>
      </div>

      {/* Right side - Image placeholder */}
      <div className="image-section">
        <span {...accessibleOnClick(handleModalClose)}>
          <Cross className="cross-icon-carousel" />
        </span>
        <div className="carousel-content">
          {slides?.map((slide, index) => (
            <div
              key={`slider-${index + 1}`}
              className={`slide ${index === activeSlide ? 'active' : ''}`}
            >
              <S3StaticImage imagePath={slide?.imagePath} alt={slide.alt} />
            </div>
          ))}
        </div>
      </div>
      <GlobalRestrictionModal
        show={showGlobalRestrictionModal}
        onClose={() => setShowGlobalRestrictionModal(false)}
        title="Creating AI Sequence is disabled in demo mode"
        content="This is a demo account. Sign up to create AI Sequences and experience how automated outreach works in Saleshandy"
        primaryBtnLabel="Sign up"
      />
    </div>
  );
};

export default memo(OnBoardingStep);
